import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, User, Briefcase, CheckCircle, Loader2, Building2, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [centers, setCenters] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: 'student',
    enrollment_mode: 'online', // online or center
    center_id: ''
  });

  // Fetch active centers for student registration
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await axios.get(`${API}/public/centers`);
        setCenters(response.data || []);
      } catch (error) {
        console.log('Could not fetch centers');
      }
    };
    fetchCenters();
  }, []);

  // Send OTP via MSG91
  const handleSendOTP = async () => {
    if (formData.mobile.length !== 10) {
      toast.error('Please enter valid 10-digit mobile number');
      return;
    }

    // Check if mobile already registered
    try {
      const checkRes = await axios.post(`${API}/auth/check-mobile`, { 
        phone: `+91${formData.mobile}` 
      });
      if (checkRes.data.exists) {
        toast.error('This mobile number is already registered. Please login.');
        return;
      }
    } catch (error) {
      // Continue if check fails
    }

    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        mobile: formData.mobile
      });
      
      setOtpSent(true);
      toast.success('OTP sent to +91' + formData.mobile);
      
      // If demo mode, show OTP
      if (response.data.status === 'demo' && response.data.otp) {
        toast.info(`Demo OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (otp.length !== 4) {
      toast.error('Please enter 4-digit OTP');
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        mobile: formData.mobile,
        otp: otp
      });
      
      if (response.data.valid) {
        setOtpVerified(true);
        toast.success('Mobile number verified!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Resend OTP
  const handleResendOTP = () => {
    setOtp('');
    handleSendOTP();
  };

  // Register
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter your name');
      return;
    }
    if (!otpVerified) {
      toast.error('Please verify your mobile number first');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      const registerData = {
        name: formData.name,
        phone: `+91${formData.mobile}`,
        password: formData.password,
        role: formData.role,
        otp_verified: true
      };
      
      // Add enrollment mode and center for students
      if (formData.role === 'student') {
        registerData.enrollment_mode = formData.enrollment_mode;
        if (formData.enrollment_mode === 'center' && formData.center_id) {
          registerData.center_id = formData.center_id;
        }
      }
      
      const response = await axios.post(`${API}/auth/register`, registerData);
      
      login(response.data.user, response.data.token);
      
      if (formData.role === 'student') {
        toast.success('Registration successful! Please complete your profile.');
        navigate('/student/profile');
      } else {
        toast.success('Registration successful! Please complete your company profile.');
        navigate('/employer/profile');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset OTP state when mobile changes
  const handleMobileChange = (value) => {
    const mobile = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, mobile });
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-blue-600">Iqra</span>
              <span className="text-emerald-600"> Skills</span>
            </h1>
            <p className="text-slate-600 mt-2">Create Your Account</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            {/* I am a - Role Selection */}
            <div>
              <Label className="text-base font-semibold">I am a *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'student' })}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    formData.role === 'student' 
                      ? 'border-blue-600 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                  data-testid="role-student-btn"
                >
                  <User className={`h-6 w-6 ${formData.role === 'student' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className="font-medium">Student</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'employer' })}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    formData.role === 'employer' 
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700' 
                      : 'border-slate-200 hover:border-emerald-300'
                  }`}
                  data-testid="role-employer-btn"
                >
                  <Briefcase className={`h-6 w-6 ${formData.role === 'employer' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <span className="font-medium">Employer</span>
                </button>
              </div>
            </div>

            {/* Enrollment Mode - Only for Students */}
            {formData.role === 'student' && (
              <div>
                <Label className="text-base font-semibold">Enrollment Mode *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, enrollment_mode: 'online', center_id: '' })}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      formData.enrollment_mode === 'online' 
                        ? 'border-purple-600 bg-purple-50 text-purple-700' 
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                    data-testid="enrollment-online-btn"
                  >
                    <Globe className={`h-5 w-5 ${formData.enrollment_mode === 'online' ? 'text-purple-600' : 'text-slate-400'}`} />
                    <div className="text-left">
                      <span className="font-medium block">Online</span>
                      <span className="text-xs text-slate-500">Direct enrollment</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, enrollment_mode: 'center' })}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      formData.enrollment_mode === 'center' 
                        ? 'border-orange-600 bg-orange-50 text-orange-700' 
                        : 'border-slate-200 hover:border-orange-300'
                    }`}
                    data-testid="enrollment-center-btn"
                  >
                    <Building2 className={`h-5 w-5 ${formData.enrollment_mode === 'center' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <div className="text-left">
                      <span className="font-medium block">Training Center</span>
                      <span className="text-xs text-slate-500">Via franchise center</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Center Selection - Only if enrollment_mode is center */}
            {formData.role === 'student' && formData.enrollment_mode === 'center' && (
              <div>
                <Label>Select Training Center *</Label>
                <select
                  value={formData.center_id}
                  onChange={(e) => setFormData({ ...formData, center_id: e.target.value })}
                  className="w-full h-10 px-3 border rounded-md bg-white mt-1"
                  required
                  data-testid="center-select"
                >
                  <option value="">-- Select a Training Center --</option>
                  {centers.map(center => (
                    <option key={center.id} value={center.id}>
                      {center.owner_name} - {center.city}, {center.state} ({center.center_code})
                    </option>
                  ))}
                </select>
                {centers.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">No training centers available. Please choose Online mode.</p>
                )}
              </div>
            )}

            {/* Name */}
            <div>
              <Label htmlFor="name">Full Name (पूरा नाम) *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
                data-testid="register-name-input"
              />
            </div>

            {/* Mobile Number with Verify Button */}
            <div>
              <Label htmlFor="mobile">Mobile Number (मोबाइल नंबर) *</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex flex-1">
                  <span className="inline-flex items-center px-3 bg-slate-100 border border-r-0 border-slate-300 rounded-l-md text-slate-600 font-medium text-sm">
                    +91
                  </span>
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="9876543210"
                    value={formData.mobile}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    className="rounded-l-none"
                    maxLength={10}
                    required
                    disabled={otpVerified}
                    data-testid="register-mobile-input"
                  />
                </div>
                {!otpVerified ? (
                  <Button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={formData.mobile.length !== 10 || sendingOtp}
                    className={`px-4 ${otpSent ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    data-testid="send-otp-btn"
                  >
                    {sendingOtp ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : otpSent ? (
                      'Resend'
                    ) : (
                      'Verify'
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center px-3 bg-emerald-100 rounded-md">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                  </div>
                )}
              </div>
              
              {/* OTP Input - Shows after OTP sent */}
              {otpSent && !otpVerified && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Label htmlFor="otp" className="text-sm">Enter OTP sent to +91{formData.mobile}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 4-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      maxLength={4}
                      className="text-center text-lg tracking-widest"
                      data-testid="otp-input"
                    />
                    <Button
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={otp.length !== 4 || verifyingOtp}
                      className="bg-blue-600 hover:bg-blue-700 px-6"
                      data-testid="verify-otp-btn"
                    >
                      {verifyingOtp ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Didn't receive OTP? <button type="button" onClick={handleResendOTP} className="text-blue-600 hover:underline">Resend</button>
                  </p>
                </div>
              )}

              {/* Verified Badge */}
              {otpVerified && (
                <div className="mt-2 flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Mobile verified successfully</span>
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
                data-testid="register-password-input"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="register-confirm-password-input"
              />
            </div>

            {/* Register Button */}
            <Button
              type="submit"
              disabled={isLoading || !otpVerified}
              className={`w-full mt-6 ${
                !otpVerified 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : formData.role === 'employer' 
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
              }`}
              data-testid="register-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  {otpVerified ? 'Create Account' : 'Verify Mobile First'}
                </>
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center text-sm text-slate-600">
            <p>
              Already have an account?{' '}
              <Link to="/auth/login" className="text-blue-600 hover:underline font-medium">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
